/**
 * BookingActions Component
 * Unified workflow-based action buttons for booking management.
 * Uses available transitions from the workflow to determine which actions to show.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  ChevronRight,
  Info,
  Kanban,
  Loader2,
  Mail,
  X,
} from 'lucide-react';
import { Button, DetailSection } from '../ui';
import { SimpleTooltip } from '../ui/tooltip';
import { StatusTransitionDialog, type EstimateLineItem } from './StatusTransitionDialog';
import type { TransitionDialogState } from '../../hooks/useStatusTransition';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import type { BookingStatus } from '../../services/statusTransitionService';
import type { StatusChecklistRequirement, UnifiedCaseType } from '@leal-crm/shared';
import { useWorkflowByType } from '../../hooks/useWorkflowConfig';
import { signatureTemplatesApi } from '../../services/signatureTemplatesApi';

// Types for available transitions from API
interface AvailableTransition {
  transitionId: string;
  toStatus: string;
  toStatusLabel: string;
  toStatusColors: { text: string; bg: string; border: string };
  toStatusMeta?: {
    id: string;
    code: string;
    name: string;
    statusType: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
  };
  dialogType: 'none' | 'confirm' | 'notes' | 'cancel' | 'complete' | 'bay_select' | 'quote';
  dialogConfig: Record<string, unknown> | null;
  minTimeInStatusMinutes?: number | null;
  maxTimeInStatusMinutes?: number | null;
  requirements: StatusChecklistRequirement[];
  canExecute: boolean;
  errors: string[];
}

interface BookingActionsProps {
  bookingId: string;
  jobId?: string | null;
  bookingReference?: string;
  customerName?: string;
  customerEmail?: string;
  bookingType?: UnifiedCaseType;
  currentStatus: string;
  prefillCustomFields?: Record<string, Record<string, unknown>>;
  isEmailVerified?: boolean;
  tokenExpires?: Date;
  onResendEmail?: () => void;
  isResendingEmail?: boolean;
  onResendNotification?: () => void;
  isResendingNotification?: boolean;
  onReschedule?: () => void;
  onCancel?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onUploadComplete?: () => void;
  showJobBoardLink?: boolean;
  compact?: boolean;
  showCustomerLinkActions?: boolean;
}

/**
 * Get action button config based on transition type
 */
function getActionConfig(transition: AvailableTransition): {
  label: string;
  icon: typeof ArrowRight;
  variant: 'default' | 'secondary' | 'destructive';
} {
  const status = transition.toStatus;
  const label = transition.toStatusLabel || status;

  // Cancellation/terminal states
  if (status === 'CANCELLED') {
    return { label, icon: X, variant: 'destructive' };
  }

  // Confirmation states
  if (status === 'CONFIRMED') {
    return { label, icon: CheckCircle, variant: 'default' };
  }

  // Dropoff flow
  if (status === 'AWAITING_DROPOFF') {
    return { label, icon: ArrowRight, variant: 'default' };
  }
  if (status === 'DROPOFF_COMPLETE') {
    return { label, icon: CheckCircle, variant: 'default' };
  }

  // Default - use the status label from workflow
  return {
    label,
    icon: ChevronRight,
    variant: 'secondary',
  };
}

function getTransitionStyle(transition: AvailableTransition) {
  const colors = transition.toStatusColors;
  if (!colors) return undefined;

  return {
    color: colors.text || undefined,
    backgroundColor: colors.bg || undefined,
    borderColor: colors.border || undefined,
  };
}

function getTransitionTooltipContent(transition: AvailableTransition) {
  if (!transition.canExecute) {
    if (transition.errors && transition.errors.length > 0) {
      return (
        <div className="space-y-1">
          <p className="font-medium">Bloqueado</p>
          <ul className="list-disc pl-4 text-xs space-y-0.5">
            {transition.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      );
    }
    return 'Transicion no disponible';
  }

  return 'Cumple requisitos para transicionar';
}

/**
 * Determine if we're in pre-arrival phase
 */
function isPreArrivalStatus(status: string): boolean {
  return ['UNVERIFIED', 'PENDING_CONFIRMATION', 'CONFIRMED', 'AWAITING_DROPOFF'].includes(status);
}

/**
 * Determine if booking is in active work phase
 */
function isActiveWorkStatus(status: string): boolean {
  return status === 'DROPOFF_COMPLETE';
}

export function BookingActions({
  bookingId,
  jobId,
  bookingReference,
  customerName,
  customerEmail,
  bookingType,
  currentStatus,
  prefillCustomFields,
  isEmailVerified = true,
  tokenExpires,
  onResendEmail,
  isResendingEmail,
  onResendNotification,
  isResendingNotification,
  onReschedule,
  onCancel,
  onSuccess,
  onError,
  onUploadComplete,
  showJobBoardLink = true,
  compact = false,
  showCustomerLinkActions = true,
}: BookingActionsProps) {
  const showVerificationAction = !isEmailVerified;
  const { success: showSuccess, error: showError } = useToast();
  const [isSendingUploadLink, setIsSendingUploadLink] = useState(false);
  const [isSendingSignatureLink, setIsSendingSignatureLink] = useState(false);
  const [dialogState, setDialogState] = useState<TransitionDialogState | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<Error | null>(null);

  const { data: workflow } = useWorkflowByType(bookingType);

  // Fetch available transitions from workflow
  const { data: transitions = [], isLoading } = useQuery<AvailableTransition[]>({
    queryKey: ['booking-transitions', bookingId, currentStatus],
    queryFn: () => api.get<AvailableTransition[]>(`/api/bookings/${bookingId}/available-transitions`),
    enabled: !!bookingId,
  });

  // Separate primary actions from cancel
  const { primaryTransitions, cancelTransition } = useMemo<{
    primaryTransitions: AvailableTransition[];
    cancelTransition: AvailableTransition | null;
  }>(() => {
    const primary: AvailableTransition[] = [];
    let cancel: AvailableTransition | null = null;

    transitions.forEach((t) => {
      if (t.toStatus === 'CANCELLED') {
        cancel = t;
      } else {
        primary.push(t);
      }
    });

    return { primaryTransitions: primary, cancelTransition: cancel };
  }, [transitions]);

  // Check special states
  const isUnverified = currentStatus === 'UNVERIFIED';
  const isTerminal = currentStatus === 'CANCELLED';
  const isPreArrival = isPreArrivalStatus(currentStatus);
  const isActiveWork = isActiveWorkStatus(currentStatus);

  // Handle transition initiation
  const handleTransitionClick = (transition: AvailableTransition) => {
    if (isTransitioning) return;
    const needsDialog =
      transition.dialogType !== 'none' ||
      (transition.requirements && transition.requirements.length > 0) ||
      (transition.errors && transition.errors.length > 0);

    if (needsDialog) {
      setDialogState({
        isOpen: true,
        bookingId,
        fromStatus: currentStatus as BookingStatus,
        toStatus: transition.toStatus as BookingStatus,
        toStatusLabel: transition.toStatusLabel,
        bookingReference: bookingReference || bookingId,
        customerName: customerName || 'Cliente',
        transitionId: transition.transitionId,
        customFieldValues: prefillCustomFields?.[transition.toStatus] || undefined,
        config: {
          dialogType: transition.dialogType,
          requirements: transition.requirements,
          dialogConfig: transition.dialogConfig || undefined,
          canExecute: transition.canExecute,
          errors: transition.errors,
        },
      });
    } else {
      // Execute immediately
      executeTransition(transition.transitionId, transition.toStatus);
    }
  };

  // Execute transition
  const executeTransition = async (
    transitionId: string,
    toStatus: string,
    data?: {
      notes?: string;
      publicNote?: string;
      reason?: string;
      bayId?: string;
      lineItems?: EstimateLineItem[];
      checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>;
      customFields?: Record<string, unknown>;
    }
  ) => {
    setIsTransitioning(true);
    setTransitionError(null);

    try {
      await api.post(`/api/bookings/${bookingId}/status`, {
        status: toStatus,
        transitionId,
        publicNote: data?.publicNote,
        internalNote: data?.notes || data?.reason,
        checklistValues: data?.checklistValues,
        transitionData: data
          ? {
              notes: data.notes,
              publicNote: data.publicNote,
              bayId: data.bayId,
              estimateLines: data.lineItems?.map((item) => ({
                description: item.description,
                type: item.category,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
                total: item.quantity * item.unitPrice,
              })),
              customFields: {
                ...data.customFields,
                cancellationReason: data.reason,
              },
            }
          : undefined,
      });

      setDialogState(null);
      onSuccess?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al cambiar estado');
      setTransitionError(error);
      onError?.(error.message);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Handle dialog execution
  const handleExecuteDialog = async (data: {
    notes?: string;
    publicNote?: string;
    reason?: string;
    bayId?: string;
    lineItems?: EstimateLineItem[];
    sendEmail?: boolean;
    checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>;
    customFields?: Record<string, unknown>;
  }) => {
    if (!dialogState) return;
    await executeTransition(dialogState.transitionId!, dialogState.toStatus, data);
  };

  // Don't show actions for terminal states
  if (isTerminal) {
    return (
      <DetailSection title="Estado">
        <div className="p-3">
          <p className="text-xs text-gray-600">
            {currentStatus === 'CANCELLED' ? 'Cita cancelada.' : 'Cita completada.'}
          </p>
          {showJobBoardLink && (
            <Link to={jobId ? `/jobs?jobId=${jobId}` : '#'} className="block mt-2">
              <Button size="sm" className="w-full" disabled={!jobId} title={!jobId ? 'Sin trabajo vinculado' : undefined}>
                <Kanban className="h-3 w-3 mr-1.5" /> Ver Trabajo
              </Button>
            </Link>
          )}
        </div>
      </DetailSection>
    );
  }

  return (
    <>
      <DetailSection title="Acciones">
        <div className={compact ? 'p-2 space-y-1.5' : 'p-3 space-y-2'}>
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Unverified email warning */}
              {isUnverified && (
                <div className="rounded bg-amber-50 border border-amber-200 p-2 mb-2">
                  <p className="text-[11px] text-amber-800">
                    Pendiente de verificación de email.
                    {tokenExpires && (
                      <> Expira: {tokenExpires.toLocaleDateString('es-ES')}</>
                    )}
                  </p>
                  {showVerificationAction && customerEmail && onResendEmail && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2 w-full"
                      onClick={onResendEmail}
                      disabled={isResendingEmail}
                    >
                      {isResendingEmail ? (
                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                      ) : (
                        <Mail className="h-3 w-3 mr-1.5" />
                      )}
                      Reenviar Email
                    </Button>
                  )}
                </div>
              )}

              {/* Primary transition actions */}
              {primaryTransitions.map((transition) => {
                const config = getActionConfig(transition);
                const Icon = config.icon;

                return (
                  <div key={transition.transitionId} className="relative">
                    <Button
                      variant={config.variant}
                      size="sm"
                      className="w-full pr-7"
                      style={getTransitionStyle(transition)}
                      onClick={() => handleTransitionClick(transition)}
                      disabled={false}
                    >
                      <Icon className="h-3 w-3 mr-1.5" />
                      {config.label}
                    </Button>
                    {!transition.canExecute && (
                      <SimpleTooltip content={getTransitionTooltipContent(transition)} side="left">
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                          <Info className="h-3.5 w-3.5" />
                        </span>
                      </SimpleTooltip>
                    )}
                  </div>
                );
              })}

              {/* JobBoard link for active work */}
              {isActiveWork && showJobBoardLink && (
                <>
                  <div className="rounded bg-blue-50 border border-blue-200 p-2">
                    <p className="text-[11px] text-blue-800">
                      Gestionar desde el tablero de trabajos.
                    </p>
                  </div>
                  <Link to={jobId ? `/jobs?jobId=${jobId}` : '#'} className="block">
                    <Button size="sm" className="w-full" disabled={!jobId} title={!jobId ? 'Sin trabajo vinculado' : undefined}>
                      <Kanban className="h-3 w-3 mr-1.5" /> Ver Trabajo
                    </Button>
                  </Link>
                </>
              )}

              {/* Secondary actions */}
              <div className="pt-2 border-t border-gray-100 space-y-1.5">
                {/* Customer links */}
                {customerEmail && showCustomerLinkActions && (
                  <div className="space-y-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={isSendingUploadLink}
                      onClick={async () => {
                        setIsSendingUploadLink(true);
                        try {
                          await api.post(`/api/bookings/${bookingId}/send-upload-link`, {});
                          showSuccess('Enlace de subida enviado');
                        } catch {
                          showError('Error al enviar enlace de subida');
                        } finally {
                          setIsSendingUploadLink(false);
                        }
                      }}
                    >
                      {isSendingUploadLink ? (
                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                      ) : (
                        <Mail className="h-3 w-3 mr-1.5" />
                      )}
                      Enviar enlace de subida
                    </Button>

                    {jobId && (
                      <SignatureLinkButton
                        jobId={jobId}
                        disabled={isSendingSignatureLink}
                        onSending={(v) => setIsSendingSignatureLink(v)}
                      />
                    )}
                  </div>
                )}

                {/* Reschedule (for pre-arrival only) */}
                {isPreArrival && onReschedule && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={onReschedule}
                  >
                    <Calendar className="h-3 w-3 mr-1.5" /> Reprogramar
                  </Button>
                )}

                {/* Cancel action */}
                {cancelTransition && (
                  <div className="relative">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full pr-7"
                      style={getTransitionStyle(cancelTransition)}
                      onClick={() => {
                        if (onCancel) {
                          // Use custom cancel handler if provided
                          onCancel();
                        } else {
                          // Use workflow transition
                          handleTransitionClick(cancelTransition);
                        }
                      }}
                      disabled={false}
                    >
                      {isUnverified ? 'Eliminar Reserva' : 'Cancelar'}
                    </Button>
                    {!cancelTransition.canExecute && (
                      <SimpleTooltip content={getTransitionTooltipContent(cancelTransition)} side="left">
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                          <Info className="h-3.5 w-3.5" />
                        </span>
                      </SimpleTooltip>
                    )}
                  </div>
                )}
              </div>

              {/* No actions available */}
              {primaryTransitions.length === 0 && !cancelTransition && !isActiveWork && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No hay acciones disponibles
                </p>
              )}
            </>
          )}
        </div>
      </DetailSection>

      {/* Resend status notification — only when verified and not terminal */}
      {customerEmail && onResendNotification && !isUnverified && !isTerminal && (
        <div className="mt-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onResendNotification}
            disabled={isResendingNotification}
          >
            {isResendingNotification ? (
              <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
            ) : (
              <Mail className="h-3 w-3 mr-1.5" />
            )}
            Reenviar email de estado
          </Button>
        </div>
      )}

      {dialogState && (
        <StatusTransitionDialog
          state={dialogState}
          isTransitioning={isTransitioning}
          error={transitionError}
          onExecute={handleExecuteDialog}
          onCancel={() => setDialogState(null)}
          onUploadComplete={onUploadComplete ? () => onUploadComplete() : undefined}
          statusMetadata={
            workflow
              ? Object.fromEntries(
                  workflow.statuses.map((status) => [
                    status.code,
                    {
                      label: status.name,
                      textColor: status.color,
                      bgColor: status.bgColor,
                      borderColor: status.borderColor,
                    },
                  ])
                )
              : undefined
          }
        />
      )}
    </>
  );
}

/**
 * SignatureLinkButton — dropdown with template picker for sending signature links
 */
function SignatureLinkButton({
  jobId,
  disabled,
  onSending,
}: {
  jobId: string;
  disabled: boolean;
  onSending: (v: boolean) => void;
}) {
  const { success: showSuccess, error: showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const { data: templates = [] } = useQuery({
    queryKey: ['signature-templates-active'],
    queryFn: () => signatureTemplatesApi.list(false),
    staleTime: 60_000,
  });

  const sendLink = async (opts: { templateId?: string; message?: string }) => {
    onSending(true);
    setIsOpen(false);
    try {
      await api.post(`/api/jobs/${jobId}/send-signature-link`, opts);
      showSuccess('Enlace de firma enviado');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al enviar enlace de firma');
    } finally {
      onSending(false);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
      >
        {disabled ? (
          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
        ) : (
          <Mail className="h-3 w-3 mr-1.5" />
        )}
        Enviar enlace de firma
      </Button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
          <div className="py-1 max-h-60 overflow-auto">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0"
                onClick={() => sendLink({ templateId: tpl.id })}
              >
                <span className="font-medium">{tpl.name}</span>
                {tpl.isDefault && (
                  <span className="ml-1 text-xs text-blue-600">(defecto)</span>
                )}
                <p className="text-xs text-gray-500 truncate mt-0.5">{tpl.body}</p>
              </button>
            ))}
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-blue-600"
              onClick={() => {
                setIsOpen(false);
                const message = prompt('Mensaje personalizado para el cliente:');
                if (message !== null) sendLink({ message });
              }}
            >
              ✏️ Mensaje personalizado...
            </button>
            {templates.length === 0 && (
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                onClick={() => sendLink({})}
              >
                Enviar sin mensaje
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingActions;
